import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import deezerAPI from './deezerApi';
import { Music, Search, X, Play, Pause, UserPlus, UserMinus, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { followersService } from '../services/followersService';
import './FriendsNew.css';

const FriendsNew = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [feedPosts, setFeedPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);

  // Estados para preview y compartir
  const [previewTrack, setPreviewTrack] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareComment, setShareComment] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Estados para filtro de amigos
  const [following, setFollowing] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null); // null = todos

  // Estados para reproducción de audio en feed
  const [playingPostId, setPlayingPostId] = useState(null);
  const [feedAudio, setFeedAudio] = useState(null);

  // Estados para likes
  const [postLikes, setPostLikes] = useState({}); // { postId: { hasLiked: bool, count: number, isLiking: bool } }

  // Ref para el audio del preview modal
  const audioRef = useRef(null);

  useEffect(() => {
    loadFollowing();
    loadFriendsFeed();

    // Cleanup: pausar audio al desmontar
    return () => {
      if (feedAudio) {
        feedAudio.pause();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.trim()) {
        searchTracks();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line
  }, [searchQuery]);

  const loadFollowing = async () => {
    try {
      const data = await followersService.getFollowing();
      setFollowing(data);
    } catch (error) {
      console.error('Error cargando following:', error);
    }
  };

  const searchTracks = async () => {
    if (!searchQuery.trim()) return;

    setSearchLoading(true);
    try {
      const result = await deezerAPI.searchTracks(searchQuery, 10);
      if (result && result.data) {
        setSearchResults(result.data);
      }
    } catch (error) {
      console.error('Error buscando tracks:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const loadFriendsFeed = async (filterUserId = null) => {
    setFeedLoading(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();

      if (!currentUser) return;

      // Obtener IDs de usuarios que sigo
      const { data: followingData } = await supabase
        .from('followers')
        .select('following_id')
        .eq('follower_id', currentUser.id);

      const followingIds = (followingData || []).map(f => f.following_id);

      // Incluir también los posts del usuario actual
      const userIdsToShow = [...followingIds, currentUser.id];

      // Query base
      let query = supabase
        .from('friend_posts')
        .select(`
          id,
          created_at,
          comment,
          user_id,
          profiles:user_id (id, email, username, avatar_url),
          tracks:track_id (id, title, artist, album, cover_image, preview_url)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      // Filtrar por usuario específico o por todos (seguidos + el usuario actual)
      if (filterUserId) {
        query = query.eq('user_id', filterUserId);
      } else {
        query = query.in('user_id', userIdsToShow);
      }

      const { data, error } = await query;

      if (error) throw error;

      console.log('📥 Posts cargados desde BD:', data);
      data?.forEach((post, index) => {
        console.log(`Post ${index + 1}:`, {
          title: post.tracks?.title,
          preview_url: post.tracks?.preview_url,
          hasPreview: !!post.tracks?.preview_url
        });
      });

      setFeedPosts(data || []);

      // Cargar likes para cada post
      if (data && data.length > 0) {
        await loadPostLikes(data.map(post => post.id));
      }
    } catch (error) {
      console.error('Error cargando feed:', error);
    } finally {
      setFeedLoading(false);
    }
  };

  const loadPostLikes = async (postIds) => {
    if (!user || postIds.length === 0) return;

    try {
      console.log('🔄 Cargando likes para posts:', postIds);

      // Obtener conteos de likes para todos los posts
      const { data: posts, error: postsError } = await supabase
        .from('friend_posts')
        .select('id, likes_count')
        .in('id', postIds);

      if (postsError) {
        console.error('❌ Error obteniendo likes_count:', postsError);
        throw postsError;
      }

      console.log('✅ Posts con likes_count:', posts);

      // Obtener likes del usuario actual
      const { data: userLikes, error: likesError } = await supabase
        .from('post_likes')
        .select('post_id')
        .in('post_id', postIds)
        .eq('user_id', user.id);

      if (likesError) {
        console.error('❌ Error obteniendo post_likes:', likesError);
        throw likesError;
      }

      console.log('✅ Likes del usuario:', userLikes);

      const userLikedPostIds = new Set(userLikes?.map(like => like.post_id) || []);

      const likesData = {};
      posts?.forEach(post => {
        likesData[post.id] = {
          hasLiked: userLikedPostIds.has(post.id),
          count: post.likes_count || 0,
          isLiking: false
        };
      });

      console.log('✅ Datos de likes procesados:', likesData);
      setPostLikes(likesData);
    } catch (error) {
      console.error('❌ Error cargando likes:', error);
    }
  };

  const handleLike = async (postId) => {
    if (!user || postLikes[postId]?.isLiking) return;

    const currentLike = postLikes[postId] || { hasLiked: false, count: 0, isLiking: false };

    // Optimistic update
    setPostLikes(prev => ({
      ...prev,
      [postId]: {
        ...currentLike,
        isLiking: true
      }
    }));

    try {
      if (currentLike.hasLiked) {
        // Unlike
        const { error: deleteError } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;

        const { error: updateError } = await supabase
          .from('friend_posts')
          .update({ likes_count: Math.max(0, currentLike.count - 1) })
          .eq('id', postId);

        if (updateError) throw updateError;

        setPostLikes(prev => ({
          ...prev,
          [postId]: {
            hasLiked: false,
            count: Math.max(0, currentLike.count - 1),
            isLiking: false
          }
        }));
      } else {
        // Like
        const { error: insertError } = await supabase
          .from('post_likes')
          .insert({
            post_id: postId,
            user_id: user.id,
          });

        if (insertError) throw insertError;

        const { error: updateError } = await supabase
          .from('friend_posts')
          .update({ likes_count: currentLike.count + 1 })
          .eq('id', postId);

        if (updateError) throw updateError;

        setPostLikes(prev => ({
          ...prev,
          [postId]: {
            hasLiked: true,
            count: currentLike.count + 1,
            isLiking: false
          }
        }));
      }
    } catch (error) {
      console.error('Error al dar like:', error);
      // Revertir optimistic update
      setPostLikes(prev => ({
        ...prev,
        [postId]: currentLike
      }));
    }
  };

  const handleTrackClick = (track) => {
    setPreviewTrack(track);
    setIsPlaying(false);
    setShareComment('');
  };

  const togglePlayPreview = () => {
    if (!audioRef.current || !previewTrack) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSkipToShare = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    setShowShareModal(true);
  };

  const handleShare = async () => {
    if (!user || !previewTrack) return;

    setSharing(true);
    try {
      console.log('🎵 Track completo de Deezer:', previewTrack);
      console.log('🎵 Preview URL:', previewTrack.preview);

      // 1. Guardar track
      const trackData = {
        id: previewTrack.id.toString(),
        title: previewTrack.title,
        artist: previewTrack.artist.name,
        album: previewTrack.album.title,
        cover_image: previewTrack.album.cover_medium,
        preview_url: previewTrack.preview || null
      };

      console.log('💾 Guardando en BD:', trackData);

      await supabase
        .from('tracks')
        .upsert(trackData, {
          onConflict: 'id',
          ignoreDuplicates: false
        });

      // 2. Crear post con comentario
      const postData = {
        user_id: user.id,
        track_id: previewTrack.id.toString(),
        comment: shareComment.trim() || null
      };

      await supabase
        .from('friend_posts')
        .insert(postData);

      // 3. Refrescar feed
      await loadFriendsFeed(selectedFilter);

      // 4. Limpiar estados
      setPreviewTrack(null);
      setShowShareModal(false);
      setShareComment('');
      setSearchQuery('');
      setSearchResults([]);

      alert('¡Canción compartida exitosamente!');
    } catch (error) {
      console.error('Error compartiendo:', error);
      alert(`Error al compartir: ${error.message}`);
    } finally {
      setSharing(false);
    }
  };

  const handleFilterChange = (userId) => {
    // Pausar audio si está reproduciéndose
    if (feedAudio) {
      feedAudio.pause();
      setFeedAudio(null);
      setPlayingPostId(null);
    }

    setSelectedFilter(userId);
    loadFriendsFeed(userId);
  };

  const handlePlayFeedPreview = async (trackId, postId) => {
    console.log('🔊 handlePlayFeedPreview llamado con:', {
      trackId,
      postId
    });

    // Si es el mismo post, pausar
    if (playingPostId === postId) {
      if (feedAudio) {
        feedAudio.pause();
      }
      setPlayingPostId(null);
      setFeedAudio(null);
      return;
    }

    // Si ya hay un audio reproduciéndose, pausarlo
    if (feedAudio) {
      feedAudio.pause();
    }

    try {
      // Obtener URL fresca del track desde Deezer
      console.log('🔄 Obteniendo URL fresca de Deezer para track:', trackId);
      const trackData = await deezerAPI.getTrackById(trackId);

      if (!trackData || !trackData.preview) {
        console.warn('⚠️ No se pudo obtener preview del track');
        alert('Esta canción no tiene preview disponible');
        return;
      }

      console.log('✅ URL fresca obtenida:', trackData.preview);

      // Reproducir nueva canción con URL fresca
      const audio = new Audio(trackData.preview);

      audio.onerror = (e) => {
        console.error('❌ Error cargando audio:', e);
        console.error('URL del audio:', trackData.preview);
        alert('No se pudo cargar el preview de esta canción');
        setPlayingPostId(null);
        setFeedAudio(null);
      };

      audio.onloadeddata = () => {
        console.log('✅ Audio cargado correctamente');
      };

      audio.play()
        .then(() => {
          console.log('▶️ Reproduciendo audio');
        })
        .catch((error) => {
          console.error('❌ Error al reproducir:', error);
          alert('No se pudo reproducir el preview');
          setPlayingPostId(null);
          setFeedAudio(null);
        });

      setFeedAudio(audio);
      setPlayingPostId(postId);

      audio.onended = () => {
        console.log('⏹️ Audio terminado');
        setPlayingPostId(null);
        setFeedAudio(null);
      };
    } catch (error) {
      console.error('❌ Error obteniendo preview:', error);
      alert('No se pudo obtener el preview de esta canción');
    }
  };

  return (
    <div className="friends-container">
      {/* CONTENIDO PRINCIPAL */}
      <main className="friends-main">
        {/* SECCIÓN DE BÚSQUEDA */}
        <section className="search-section">
          <h2>
            <Music size={24} />
            Buscar y Compartir Música
          </h2>

          <div className="search-input-wrapper">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar canciones..."
              className="search-input"
            />
          </div>

          {searchLoading && (
            <div className="search-loading">Buscando...</div>
          )}

          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((track) => (
                <div
                  key={track.id}
                  className="search-track-card"
                  onClick={() => handleTrackClick(track)}
                >
                  <img
                    src={track.album.cover_medium}
                    alt={track.title}
                    className="track-cover"
                  />
                  <div className="track-info">
                    <div className="track-title">{track.title}</div>
                    <div className="track-artist">{track.artist.name}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!searchLoading && searchQuery && searchResults.length === 0 && (
            <div className="no-results">No se encontraron resultados</div>
          )}
        </section>

        {/* SECCIÓN DE FEED */}
        <section className="feed-section">
          <h2>Feed de Amigos</h2>

          {feedLoading ? (
            <div className="feed-loading">Cargando feed...</div>
          ) : feedPosts.length === 0 ? (
            <div className="feed-empty">
              <Music size={48} />
              <p>No hay publicaciones aún</p>
              <p className="empty-subtitle">
                {selectedFilter ? 'Este usuario no ha compartido música' : '¡Sigue a alguien para ver su música!'}
              </p>
            </div>
          ) : (
            <div className="feed-posts">
              {feedPosts.map((post) => {
                console.log('🎨 Renderizando post:', {
                  id: post.id,
                  title: post.tracks?.title,
                  preview_url: post.tracks?.preview_url,
                  hasPreview: !!post.tracks?.preview_url
                });
                return (
                <article key={post.id} className="feed-post">
                  <div className="post-header">
                    {post.profiles?.avatar_url ? (
                      <img src={post.profiles.avatar_url} alt="" className="post-user-avatar" />
                    ) : (
                      <div className="post-user-avatar-placeholder">
                        {post.profiles?.email?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="post-user-info">
                      <div className="post-username">
                        {post.profiles?.username || post.profiles?.email?.split('@')[0] || 'Usuario'}
                      </div>
                      <div className="post-date">
                        {new Date(post.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="post-content">
                    <div className="post-track">
                      <img
                        src={post.tracks?.cover_image}
                        alt={post.tracks?.title}
                        className="post-track-cover"
                      />
                      {post.tracks?.id && (
                        <div
                          className="post-track-play"
                          onClick={() => handlePlayFeedPreview(post.tracks.id, post.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          {playingPostId === post.id ? <Pause size={24} /> : <Play size={24} />}
                        </div>
                      )}
                    </div>

                    <div className="post-comment-section">
                      <div className="post-track-info">
                        <div className="post-track-header">
                          <div className="post-track-title">{post.tracks?.title}</div>
                          <button
                            className={`post-like-btn ${postLikes[post.id]?.hasLiked ? 'liked' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLike(post.id);
                            }}
                            disabled={postLikes[post.id]?.isLiking}
                          >
                            <Heart
                              size={14}
                              fill={postLikes[post.id]?.hasLiked ? '#0064c8' : 'none'}
                            />
                            {postLikes[post.id]?.count || 0}
                          </button>
                        </div>
                        <div className="post-track-artist">{post.tracks?.artist}</div>
                      </div>
                      {post.comment && (
                        <div className="post-comment">
                          "{post.comment}"
                        </div>
                      )}
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* MODAL DE PREVIEW (PANTALLA BORROSA) */}
      {previewTrack && !showShareModal && (
        <div className="preview-modal">
          <div className="preview-backdrop" onClick={() => setPreviewTrack(null)} />
          <div className="preview-content">
            <button className="preview-close" onClick={() => setPreviewTrack(null)}>
              <X size={24} />
            </button>

            <img
              src={previewTrack.album.cover_big}
              alt={previewTrack.title}
              className="preview-cover"
            />

            <h3 className="preview-title">{previewTrack.title}</h3>
            <p className="preview-artist">{previewTrack.artist.name}</p>

            {previewTrack.preview && (
              <>
                <audio
                  ref={audioRef}
                  src={previewTrack.preview}
                  onEnded={() => setIsPlaying(false)}
                />
                <button className="preview-play-btn" onClick={togglePlayPreview}>
                  {isPlaying ? <Pause size={48} /> : <Play size={48} />}
                </button>
              </>
            )}

            <button className="preview-share-btn" onClick={handleSkipToShare}>
              Continuar para compartir
            </button>
          </div>
        </div>
      )}

      {/* MODAL DE COMPARTIR */}
      {showShareModal && previewTrack && (
        <div className="share-modal">
          <div className="share-backdrop" onClick={() => setShowShareModal(false)} />
          <div className="share-content">
            <button className="share-close" onClick={() => setShowShareModal(false)}>
              <X size={24} />
            </button>

            <h2>Compartir con tus amigos</h2>

            <div className="share-layout">
              <div className="share-left">
                <img
                  src={previewTrack.album.cover_medium}
                  alt={previewTrack.title}
                  className="share-cover"
                />
                <div className="share-track-info">
                  <div className="share-track-title">{previewTrack.title}</div>
                  <div className="share-track-artist">{previewTrack.artist.name}</div>
                </div>
                {previewTrack.preview && (
                  <button className="share-preview-btn" onClick={togglePlayPreview}>
                    {isPlaying ? <><Pause size={20} /> Pausar</> : <><Play size={20} /> Escuchar</>}
                  </button>
                )}
              </div>

              <div className="share-right">
                <label htmlFor="comment">¿Qué piensas de esta canción?</label>
                <textarea
                  id="comment"
                  value={shareComment}
                  onChange={(e) => setShareComment(e.target.value)}
                  placeholder="Comparte tu opinión..."
                  maxLength={500}
                  rows={6}
                />
                <div className="char-count">{shareComment.length}/500</div>

                <button
                  className="share-submit-btn"
                  onClick={handleShare}
                  disabled={sharing}
                >
                  {sharing ? 'Compartiendo...' : 'Compartir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendsNew;
